/**
 * Everything the app has to do about push, in one place (#1642).
 *
 * Three jobs, and they are separate effects because they have different
 * lifetimes:
 *
 *   · File this device when somebody signs in, forget it when they sign out.
 *   · Deliver the notification that LAUNCHED the app, which no listener was
 *     alive to hear.
 *
 * ⚠️ NAVIGATION IS NOT HERE. A tap reaches JS as an ordinary `Linking` url
 * event — the native side forwards `deepLink` through `RCTLinkingManager` —
 * so `deepLinkService` and `App.tsx`'s existing deep-link effect handle it
 * unchanged, including the "not signed in yet, save it as a return URL" path
 * they already implement. The only gap that needed filling is the COLD START,
 * below.
 *
 * ⚠️ NOTHING HERE BLOCKS OR THROWS. Every call answers a result object;
 * registration failing must never break a login, and un-registration failing
 * must never hold up a logout.
 *
 * @see src/services/push/pushRegistration.ts
 * @see ios/vertiv/AppDelegate.swift
 */

import { useEffect } from 'react';

import { deepLinkService } from '../services/deepLinkService';
import {
  registerDevice,
  syncBadge,
  unregisterDevice,
} from '../services/push/pushRegistration';
import { pushService } from '../services/push/pushService';

export interface UsePushNotificationsOptions {
  isAuthenticated: boolean;
}

export const usePushNotifications = ({
  isAuthenticated,
}: UsePushNotificationsOptions): void => {
  /**
   * Register on sign-in, unregister on sign-out.
   *
   * ⚠️ THE CLEANUP DOES NOT UNREGISTER. React runs cleanup on unmount and on
   * every dependency change, including a re-login; unregistering there would
   * delete the row this effect had just created. Sign-out is the `else`
   * branch, which runs exactly when the person is no longer authenticated.
   */
  useEffect(() => {
    let cancelled = false;

    if (isAuthenticated) {
      registerDevice().then(outcome => {
        if (cancelled) return;
        if (outcome.status === 'failed') {
          console.warn('📱 Push registration failed:', outcome.reason);
        } else if (__DEV__) {
          // ⚠️ EVERY OUTCOME, NOT JUST `failed`. `no-token`, `permission` and
          // `unsupported` are all silent refusals to register, and silence is
          // indistinguishable from success from the outside — which cost an
          // hour on Android, where the device simply never appeared and
          // nothing anywhere said why. The status alone is enough; the token
          // itself must never reach a log.
          console.log('📱 Push registration outcome:', outcome.status);
        }
        return syncBadge();
      });
    } else {
      unregisterDevice().then(outcome => {
        if (outcome.status === 'failed') {
          console.warn('📱 Push unregistration failed:', outcome.reason);
        }
      });
    }

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  /**
   * ⚠️ THE BADGE REFRESH ON FOREGROUND IS NOT HERE, AND IT IS NOT AN
   * OVERSIGHT. `useAppStateManager` documents itself as "should only be used
   * ONCE in your app" and warns at runtime about duplicate listeners;
   * `AuthContext` already holds the single instance. So `syncBadge()` is
   * called from that one `onActive`, next to the session validation, and this
   * comment is here so the next reader does not go looking for it.
   */

  /**
   * The notification that launched the app.
   *
   * ⚠️ READ ONCE, ON MOUNT, AND THE NATIVE SIDE CLEARS IT. By the time React
   * has mounted and `deepLinkService` has a listener, a cold-start tap has
   * already been and gone — this is the only way that link is ever seen.
   * Missing it is why "tapping a notification opens the app but not the
   * request" is such a common bug.
   */
  useEffect(() => {
    let cancelled = false;

    pushService.getInitialNotification().then(initial => {
      if (cancelled || !initial?.deepLink) return;
      // Into the SAME chain a universal link takes, so the parse, the
      // routing and the "not signed in yet" branch are not re-implemented
      // here and cannot drift from it.
      deepLinkService.handleDeepLink(initial.deepLink);
    });

    return () => {
      cancelled = true;
    };
  }, []);
};
